import * as React from 'react';
import { Height } from './ChromeTooltipArrow';
import { BorderWidth, Padding } from './ChromeTooltipBox';
import { ChromeTooltipDisplay } from './ChromeTooltipDisplay';
import { assertNever, ClosedRectangle, containsClientRect, getNextSideClockwise, isHorizontalSide, NumberOfSides } from './ChromeTooltipUtility';

// extra space for mousing between target and tooltip
const MouseSpace = 1;

export const DefaultOffset = BorderWidth + Padding + Height / 2;

export type ChromeTooltipSide = 'Top' | 'Bottom' | 'Left' | 'Right';

const DefaultSide: ChromeTooltipSide = 'Right';

const Style: React.CSSProperties = {
  position: 'relative',
  display: 'inline-block'
};

export interface ChromeTooltipProps {
  /** Function for getting tooltip content */
  readonly getContent: () => Promise<JSX.Element | null>;
  /** Desired width of tooltip content, note that max width is InnerMaxWidth */
  readonly width?: number;
  /** Element to position tooltip within, document body is used if none is specified */
  readonly boundaryElement?: HTMLElement;
  /** Default side of target that tooltip should be displayed on */
  readonly defaultSide?: ChromeTooltipSide;
  // TODO remove after unifying stacking strategy in viz client, see feature 579115
  readonly zIndex?: number;
}

/**
 * State for chrome tooltip.
 */
export interface ChromeTooltipState {
  /** Content of tooltip, which is non-null when tooltip is being displayed */
  readonly content?: JSX.Element | null;
  /** Check if tooltip has been positioned */
  readonly positioned: boolean;
  /** Side of target that tooltip should be displayed on */
  readonly side: ChromeTooltipSide;
  /** Offset from top or left of tooltip to arrow, must be greater than or equal to half of arrow height */
  readonly offset: number;
}

export class ChromeTooltipWidget extends React.Component<ChromeTooltipProps, ChromeTooltipState> {
  private targetElement: HTMLElement | null = null;
  private tooltipElement: HTMLElement | null = null;
  private canceled: boolean = false;

  public constructor(props: ChromeTooltipProps) {
    super(props);
    this.state = {
      content: null,
      positioned: false,
      side: props.defaultSide || DefaultSide,
      offset: DefaultOffset
    };
  }

  private setContent = (content?: JSX.Element | null): void => {
    if (!this.canceled) {
      this.setState(prevState => prevState.content ? prevState : { ...prevState, content });
    }
  }

  private open = (): void => {
    // tslint:disable-next-line:no-floating-promises
    this.props.getContent().then(this.setContent);
  }

  private close = (): void => {
    this.setState({ content: null, positioned: false });
  }

  private handleClick = (e: React.MouseEvent<HTMLSpanElement>): void => {
    this.state.content ? this.close() : this.open();
    e.preventDefault();
  }

  private handleMouseEnter = (e: React.MouseEvent<HTMLSpanElement>): void => {
    if (!this.state.content) {
      this.canceled = false;
      this.open();
    }
  }

  private handleMouseLeave = ({ clientX, clientY }: React.MouseEvent<HTMLElement>): void => {
    if (!this.state.content) {
      // TFSID 740766: cancel if mouse outside target before finished fetching content
      this.canceled = true;
    }
    if (this.tooltipElement) {
      let { left, right, top, bottom } = this.tooltipElement.getBoundingClientRect();
      // add offset based on side
      switch (this.state.side) {
        case 'Top':
          bottom += MouseSpace;
          break;
        case 'Bottom':
          top -= MouseSpace;
          break;
        case 'Left':
          right += MouseSpace;
          break;
        case 'Right':
          left -= MouseSpace;
          break;
        default:
          assertNever(this.state.side);
      }
      // close if mouse outside of tooltip
      let rectangle = new ClosedRectangle(left, right, top, bottom);
      if (!rectangle.containsPoint(clientX, clientY)) {
        this.close();
      }
    }
  }

  public componentDidUpdate(): void {
    if (this.state.content && !this.state.positioned) {
      if (!this.targetElement || !this.tooltipElement) {
        throw new Error('reference to target or tooltip isn\'t set!');
      }
      let outerRect = (this.props.boundaryElement || document.body).getBoundingClientRect();
      let innerRect = this.targetElement.getBoundingClientRect();
      // try to position tooltip within outer rectangle
      let { width, height } = this.tooltipElement.getBoundingClientRect();
      // try default side first
      let side = this.props.defaultSide || DefaultSide;
      let offset = this.state.offset;
      let i = 0;
      // tslint:disable-next-line: deprecation
      let rect = ChromeTooltipWidget.computeTooltipRectangle(innerRect, width, height, side, offset);
      while (i < NumberOfSides && !containsClientRect(outerRect, rect)) {
        i++;
        side = getNextSideClockwise(side);
        // tslint:disable-next-line: deprecation
        rect = ChromeTooltipWidget.computeTooltipRectangle(innerRect, width, height, side, offset);
      }
      this.setState({ positioned: true, side });
    }
  }

  public componentDidMount(): void {
    this.canceled = false;
  }

  // NOTE cancel any unresolved promise of content to avoid setting state on unmounted component,
  // see https://facebook.github.io/react/blog/2015/12/16/ismounted-antipattern.html for more information
  public componentWillUnmount(): void {
    this.canceled = true;
  }

  private updateTargetRef = (element: HTMLDivElement | null): void => {
    this.targetElement = element;
  }

  private updateTooltipRef = (element: HTMLElement | null): void => {
    this.tooltipElement = element;
  }

  public render(): React.ReactElement {
    return (
      <div
        onClick={this.handleClick}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        ref={this.updateTargetRef}
        style={Style}
      >
        {this.props.children}
        {this.renderTooltip()}
      </div>
    );
  }

  private renderTooltip(): JSX.Element | null {
    if (this.state.content && this.targetElement) {
      return <ChromeTooltipDisplay content={this.state.content}
        width={this.props.width}
        targetElement={this.targetElement}
        handleRef={this.updateTooltipRef}
        close={this.close}
        positioned={this.state.positioned}
        side={this.state.side}
        offset={this.state.offset}
        zIndex={this.props.zIndex} />;
    } else {
      return null;
    }
  }

  // tslint:disable-next-line:max-line-length
  private static computeTooltipRectangle(target: ClientRect, width: number, height: number, side: ChromeTooltipSide, offset: number): ClientRect {
    let top: number;
    let left: number;
    if (isHorizontalSide(side)) {
      top = target.top + target.height / 2 - offset;
      left = side === 'Left' ? target.left - width : target.right;
    } else {
      left = target.left + target.width / 2 - offset;
      top = side === 'Top' ? target.top - height : target.bottom;
    }
    return {
      top,
      bottom: top + height,
      left,
      right: left + width,
      width,
      height
    } as ClientRect;
  }
}
